import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ClientFormStatus } from '../lib/types';
import { cx } from '../lib/utils';

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="spinner-wrap" role="status">
      <span className="spinner" aria-hidden />
      <span className="muted">{label}</span>
    </div>
  );
}

export function ErrorState({ message, action }: { message: string; action?: ReactNode }) {
  return (
    <div className="state-box state-error">
      <p>{message}</p>
      {action}
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="state-box">
      <h3>{title}</h3>
      {children}
    </div>
  );
}

const STATUS_LABEL: Record<ClientFormStatus, string> = {
  pending: 'Not started',
  in_progress: 'In progress',
  submitted: 'Submitted',
};

export function StatusBadge({
  status,
  isActive = true,
}: {
  status: ClientFormStatus;
  isActive?: boolean;
}) {
  if (!isActive) return <span className="badge badge-muted">Disabled</span>;
  return <span className={cx('badge', `badge-${status}`)}>{STATUS_LABEL[status]}</span>;
}

export function ProgressBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="progress" aria-label={`${pct}% complete`}>
      <div className="progress-bar" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle && <p className="muted">{subtitle}</p>}
      </div>
      {actions && <div className="row">{actions}</div>}
    </div>
  );
}

/** Small dropdown menu: closes on outside click, Escape, or after an item is chosen. */
export function Menu({
  label,
  trigger,
  triggerClassName = 'icon-btn',
  align = 'right',
  children,
}: {
  /** Accessible name / tooltip of the trigger button. */
  label: string;
  trigger: ReactNode;
  triggerClassName?: string;
  align?: 'left' | 'right';
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="menu" ref={ref}>
      <button
        type="button"
        className={triggerClassName}
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {trigger}
      </button>
      {open && (
        // Any item click bubbles up here and closes the menu.
        <div
          className={cx('menu-list', `menu-${align}`)}
          role="menu"
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function MenuItem({
  onSelect,
  danger,
  disabled,
  hint,
  children,
}: {
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cx('menu-item', danger && 'danger')}
      disabled={disabled}
      onClick={onSelect}
    >
      <span>{children}</span>
      {hint && <span className="menu-hint">{hint}</span>}
    </button>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="menu-label">{children}</div>;
}
